import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Store,
  Calendar,
  FileText,
  Banknote,
  MessageSquare,
  Search,
  ChevronDown,
} from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import { SaveRevenue, RevenueData, ValidateSales } from "../../../services/revenueservice";
import { cx, toLocalISOString } from "../../../utils";
import { fetchdropdownMall, fetchdropdownTenant } from "../../../services/dropdown";

interface MallOption {
  value: string;
  label: string;
}

interface TenantOption {
  value: string;
  label: string;
}

export default function RevenueTransaction() {
  const navigate = useNavigate();
  const [transactionDate, setTransactionDate] = useState<Date | null>(null);
  const [amount, setAmount] = useState("");
  const [remarks, setRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [mallOptions, setMallOptions] = useState<MallOption[]>([]);
  const [selectedMall, setSelectedMall] = useState("");
  const [mallSearchQuery, setMallSearchQuery] = useState("");
  const [isMallDropdownOpen, setIsMallDropdownOpen] = useState(false);

  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState("");
  const [tenantSearchQuery, setTenantSearchQuery] = useState("");
  const [isTenantDropdownOpen, setIsTenantDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchMalls = async () => {
      try {
        const response = await fetchdropdownMall(0);
        if (!response?.dropdownClassDatas) return;
        const options: MallOption[] = response.dropdownClassDatas.map((m: { DataId: number; DataLabel: string }) => ({
          value: m.DataId.toString(),
          label: m.DataLabel,
        }));
        setMallOptions(options);
        const storedMallId = localStorage.getItem("mall_id") || sessionStorage.getItem("mall_id");
        if (storedMallId && options.find((o) => o.value === storedMallId)) {
          // Gunakan mall yang tersimpan di storage
          setSelectedMall(storedMallId);
        } else if (options.length === 1) {
          // Auto-select jika hanya ada 1 mall (kasus tenant biasa)
          setSelectedMall(options[0].value);
          localStorage.setItem("mall_id", options[0].value);
        } else {
          // HO dengan banyak mall — biarkan user pilih
          setSelectedMall("");
        }
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch mall options" });
      }
    };
    fetchMalls();
  }, []);

  useEffect(() => {
    if (!selectedMall) {
      setTenantOptions([]);
      setSelectedTenant("");
      return;
    }
    const fetchTenants = async () => {
      try {
        const response = await fetchdropdownTenant(parseInt(selectedMall, 10));
        if (!response?.dropdownClassDatas) return;
        const options: TenantOption[] = response.dropdownClassDatas.map((t: { DataId: number; DataLabel: string }) => ({
          value: t.DataId.toString(),
          label: t.DataLabel,
        }));
        setTenantOptions(options);
        if (options.length === 1) {
          setSelectedTenant(options[0].value);
          localStorage.setItem("tenant_id", options[0].value);
        } else {
          const storedTenantId = localStorage.getItem("tenant_id") || sessionStorage.getItem("tenant_id");
          if (storedTenantId && options.find((o) => o.value === storedTenantId)) {
            setSelectedTenant(storedTenantId);
          } else {
            setSelectedTenant("");
          }
        }
      } catch {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to fetch tenant options" });
      }
    };
    fetchTenants();
  }, [selectedMall]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".mall-dropdown-container")) {
        setIsMallDropdownOpen(false);
        setMallSearchQuery("");
      }
      if (!target.closest(".tenant-dropdown-container")) {
        setIsTenantDropdownOpen(false);
        setTenantSearchQuery("");
      }
    };
    if (isMallDropdownOpen || isTenantDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMallDropdownOpen, isTenantDropdownOpen]);

  const filteredMallOptions = useMemo(() => {
    if (!mallSearchQuery) return mallOptions;
    return mallOptions.filter((o) => o.label.toLowerCase().includes(mallSearchQuery.toLowerCase()));
  }, [mallOptions, mallSearchQuery]);

  const filteredTenantOptions = useMemo(() => {
    if (!tenantSearchQuery) return tenantOptions;
    return tenantOptions.filter((o) => o.label.toLowerCase().includes(tenantSearchQuery.toLowerCase()));
  }, [tenantOptions, tenantSearchQuery]);

  const selectedMallLabel = mallOptions.find((o) => o.value === selectedMall)?.label || "Select Mall";
  const selectedTenantLabel = tenantOptions.find((o) => o.value === selectedTenant)?.label || "Select Store";

  const isMallDisabled = mallOptions.length === 1;
  const isTenantDisabled = !selectedMall || tenantOptions.length === 0 || tenantOptions.length === 1;

  const autoTransactionNumber = useMemo(() => {
    if (!selectedTenant || !transactionDate) return "";
    const now = new Date();
    const year = transactionDate.getFullYear();
    const month = String(transactionDate.getMonth() + 1).padStart(2, "0");
    const day = String(transactionDate.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const formattedDate = `${year}${month}${day}${hours}${minutes}${seconds}`;
    return `TRX-${selectedTenant}-${formattedDate}`;
  }, [selectedTenant, transactionDate]);

  const handleMallSelect = (value: string) => {
    setSelectedMall(value);
    localStorage.setItem("mall_id", value);
    setIsMallDropdownOpen(false);
    setMallSearchQuery("");
    setSelectedTenant("");
  };

  const handleTenantSelect = (value: string) => {
    setSelectedTenant(value);
    localStorage.setItem("tenant_id", value);
    setIsTenantDropdownOpen(false);
    setTenantSearchQuery("");
  };

  const handleSave = async () => {
    if (!selectedMall) {
      Swal.fire({ icon: "warning", title: "Warning", text: "Please select a mall first." });
      return;
    }
    if (!selectedTenant) {
      Swal.fire({ icon: "warning", title: "Warning", text: "Please select a store first." });
      return;
    }
    if (!autoTransactionNumber || !amount) {
      Swal.fire({ icon: "warning", title: "Warning", text: "Please fill in all required fields." });
      return;
    }

    setIsSubmitting(true);
    try {
      const filterDateStr = toLocalISOString(transactionDate!).substring(0, 10);
      const validationResult = await ValidateSales(parseInt(selectedTenant, 10), filterDateStr);

      const data = validationResult?.Content ?? validationResult;
      const hasExisting = data?.Code === 1;

      if (hasExisting) {
        const confirmMessage = data?.Message
          ? data.Message.replace("upload sales ulang", "input manual ulang")
          : `Data transaksi untuk tanggal ${filterDateStr} sudah ada.`;
        const confirmResult = await Swal.fire({
          title: "Konfirmasi",
          text: `${confirmMessage} Apakah Anda yakin ingin melanjutkan penyimpanan?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Ya, lanjut",
          cancelButtonText: "Tidak, lihat mall traffic",
        });

        if (!confirmResult.isConfirmed) {
          setIsSubmitting(false);
          navigate("/tenant-relation/visitor");
          return;
        }
      }

      const revenueData: RevenueData[] = [
        {
          RevenueId: 0,
          TransactionNumber: autoTransactionNumber,
          TransactionDate: toLocalISOString(transactionDate!),
          Amount: parseFloat(amount),
          Remarks: remarks,
        },
      ];

      const result = await SaveRevenue(parseInt(selectedTenant), revenueData);

      if (!result) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to connect to server." });
        return;
      }

      Swal.fire(
        result.TotalSuccess > 0 ? result.Message : "Upload Data Error",
        `<div><p>Total Success: ${result.TotalSuccess}</p><p>Total Error: ${result.TotalError}</p></div>`,
        result.TotalError === 0 ? "success" : "warning"
      );

      if (result.TotalError === 0) {
        handleReset();
      }
    } catch (error) {
      console.log(error);
      Swal.fire({ icon: "error", title: "Error", text: "An error occurred during transaction processing." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSelectedTenant("");
    setTransactionDate(null);
    setAmount("");
    setRemarks("");
  };

  return (
    <div className="space-y-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div className="space-y-2 mall-dropdown-container relative">
              <label className="block text-sm font-semibold text-gray-700 dark:text-white">
                Mall <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => !isMallDisabled && setIsMallDropdownOpen(!isMallDropdownOpen)}
                  disabled={isMallDisabled}
                  className={cx(
                    "w-full px-4 py-3 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white flex items-center justify-between",
                    isMallDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                  )}
                >
                  <span className={!selectedMall ? "text-gray-400" : ""}>{selectedMallLabel}</span>
                  <ChevronDown className={cx("w-4 h-4 text-gray-400 transition-transform", isMallDropdownOpen && "rotate-180")} />
                </button>
                {isMallDropdownOpen && !isMallDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={mallSearchQuery}
                          onChange={(e) => setMallSearchQuery(e.target.value)}
                          placeholder="Search mall..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredMallOptions.length > 0 ? (
                        filteredMallOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleMallSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedMall === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">No mall found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 tenant-dropdown-container relative">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Store className="w-4 h-4 text-indigo-600" />
                Store <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => !isTenantDisabled && setIsTenantDropdownOpen(!isTenantDropdownOpen)}
                  disabled={isTenantDisabled}
                  className={cx(
                    "w-full px-4 py-3 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-left focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white flex items-center justify-between",
                    isTenantDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"
                  )}
                >
                  <span className={!selectedTenant ? "text-gray-400" : ""}>{selectedTenantLabel}</span>
                  <ChevronDown className={cx("w-4 h-4 text-gray-400 transition-transform", isTenantDropdownOpen && "rotate-180")} />
                </button>
                {isTenantDropdownOpen && !isTenantDisabled && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-hidden">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                          type="text"
                          value={tenantSearchQuery}
                          onChange={(e) => setTenantSearchQuery(e.target.value)}
                          placeholder="Search store..."
                          className="w-full pl-9 pr-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-gray-100"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {filteredTenantOptions.length > 0 ? (
                        filteredTenantOptions.map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleTenantSelect(option.value)}
                            className={cx(
                              "w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
                              selectedTenant === option.value
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                                : "text-gray-700 dark:text-gray-300"
                            )}
                          >
                            {option.label}
                          </button>
                        ))
                      ) : (
                        <div className="px-4 py-6 text-sm text-center text-gray-500 dark:text-gray-400">No store found</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label htmlFor="transactionDate" className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Calendar className="w-4 h-4 text-indigo-600" />
                Transaction Date <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <DatePicker
                  selected={transactionDate}
                  onChange={(date) => setTransactionDate(date as Date)}
                  dateFormat="dd MMM yyyy"
                  placeholderText="Select transaction date"
                  popperPlacement="bottom-start"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 outline-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  wrapperClassName="w-full"
                  calendarClassName="shadow-xl border-0"
                />
                <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              </div>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <FileText className="w-4 h-4 text-indigo-600" />
                Transaction Number <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                readOnly
                value={autoTransactionNumber}
                placeholder="Select date first"
                className="w-full px-4 py-3 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 cursor-not-allowed select-none outline-none"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <Banknote className="w-4 h-4 text-indigo-600" />
                Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">Rp</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0"
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-white">
                <MessageSquare className="w-4 h-4 text-indigo-600" />
                Remarks
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Add any additional notes or remarks..."
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 placeholder-gray-400 resize-none dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-3 sm:gap-4 p-4 sm:p-6">
          <button
            onClick={handleSave}
            disabled={isSubmitting}
            className="flex-1 bg-blue-600 text-white font-semibold py-3 sm:py-4 rounded-xl shadow-lg hover:bg-blue-700 hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Save"}
          </button>
          <button
            onClick={handleReset}
            className="px-6 sm:px-8 bg-gray-200 text-gray-700 font-semibold py-3 sm:py-4 rounded-xl hover:bg-gray-300 transition-all"
          >
            Reset
          </button>
        </div>
      </div>
    </div>
  );
}